/**
 * ADR-0013 — Sale void / line void planning (inverse of checkout consume).
 * Pure helpers on parsed business payload — no separate KV reads.
 */

import { ensureLotsArray, LOT_STATUS } from '../../../utils/lotSystem';
import { sameLotId, sameProductId } from '../../../utils/productIdUtils';
import { projectStockFromLots } from '../../inventory/stock';
import {
  STOCK_MOVEMENT_TYPES,
  appendStockMovements,
  normalizeStockMovement
} from '../../inventory/movements';
import { packagingAuditMetaFromCatalog } from '../../inventory/packaging/baseUnits';
import {
  resolveSaleUnpaidAmount,
  applyCustomerBalanceDelta
} from '../../customers/balance/saleBalance';
import {
  publishLotUpdated,
  publishLegacyWindowEvent
} from '../../../infrastructure/events/legacyEventBridge';
import {
  getSaleLineActiveQuantity,
  getSaleLineUnitPrice,
  getSaleLineActiveAmount,
  isSaleFullyCancelled,
  computeSaleActiveTotal,
  resolvePersistedSaleTotals
} from '../saleLineMath';
import { resolveDishIngredientDemand } from '../../restaurant/dishIncludedStock';
import { isRestaurantScopedSale } from '../../restaurant/restaurantContext';
import { isRestaurantDishProduct } from '../../../utils/restaurantMode';

export {
  getSaleLineActiveQuantity,
  getSaleLineUnitPrice,
  getSaleLineActiveAmount,
  isSaleFullyCancelled,
  computeSaleActiveTotal,
  resolvePersistedSaleTotals
};

function saleRefs(sale) {
  const refs = new Set();
  if (sale?.orderId != null && sale.orderId !== '') refs.add(String(sale.orderId));
  if (sale?.id != null && sale.id !== '') refs.add(String(sale.id));
  return refs;
}

function matchesSaleRef(referenceId, refs) {
  if (referenceId == null || referenceId === '') return false;
  return refs.has(String(referenceId));
}

function lineKey(product, index) {
  if (product?.lineId != null && product.lineId !== '') return String(product.lineId);
  if (product?.id != null && product.id !== '' && String(product.id) !== String(product.productId)) {
    return String(product.id);
  }
  return `idx:${index}`;
}

/**
 * Dish detection + per-portion stock demand — same XOR as checkout (recipe / includes).
 * Returns null for non-dish; array of { productId, quantity } per sold unit (caller × void qty).
 * @param {object} productSaleLine
 * @param {object[]} catalogProducts
 * @param {unknown[]} [recipes]
 */
function resolveVoidDishIncludeLinks(productSaleLine, catalogProducts = [], recipes = []) {
  const catalog = Array.isArray(catalogProducts) ? catalogProducts : [];
  const catalogProduct =
    catalog.find((p) =>
      sameProductId(p?.id ?? p?.productId, productSaleLine?.productId)
    ) || null;
  const isDish =
    productSaleLine?.isRestaurantDish === true ||
    isRestaurantDishProduct(productSaleLine) ||
    isRestaurantDishProduct(catalogProduct);
  if (!isDish) return null;
  // portionsSold=1 → absolute demand for one sale unit (void multiplies by voided qty)
  const demand = resolveDishIngredientDemand(productSaleLine, catalogProduct, 1, {
    recipes,
    catalogProducts: catalog
  });
  return demand.map((row) => ({
    productId: row.productId,
    quantity: row.quantity
  }));
}

/** ADR-0016: scale frozen COGS for restaurant and magasin freezes. */
function shouldScaleFrozenInventoryCost(sale) {
  return (
    sale?.inventoryCostFrozen === true ||
    sale?.costSnapshot?.frozen === true ||
    sale?.restaurantInventoryCost === true ||
    isRestaurantScopedSale(sale)
  );
}

/** @deprecated use shouldScaleFrozenInventoryCost */
function shouldScaleRestaurantInventoryCost(sale) {
  return shouldScaleFrozenInventoryCost(sale);
}

/**
 * Lot restores still owed for this sale (from SALE movements − existing SALE_RETURN).
 * @returns {Array<{ lotId: string, productId: *, location: string, quantity: number, unitCost?: number }>}
 */
export function collectRemainingSaleDeductions(sale, stockMovements = []) {
  const refs = saleRefs(sale);
  if (refs.size === 0) return [];

  /** @type {Map<string, { lotId: string, productId: *, location: string, quantity: number, unitCost: number }>} */
  const byLot = new Map();

  const bump = (lotId, productId, location, qty, unitCost = 0) => {
    if (!lotId || !(qty > 0)) return;
    const key = String(lotId);
    const cost = Number(unitCost) || 0;
    const prev = byLot.get(key);
    if (prev) {
      prev.quantity += qty;
      if (!(prev.unitCost > 0) && cost > 0) prev.unitCost = cost;
    } else {
      byLot.set(key, {
        lotId: key,
        productId,
        location: location || 'Principal',
        quantity: qty,
        unitCost: cost
      });
    }
  };

  for (const m of stockMovements || []) {
    if (!matchesSaleRef(m?.referenceId, refs)) continue;
    const type = String(m.movementType || '');
    const lotId = m.lotId != null ? String(m.lotId) : null;
    const absQty = Math.abs(Number(m.quantity) || 0);
    if (!lotId || !(absQty > 0)) continue;

    if (type === STOCK_MOVEMENT_TYPES.SALE || type === 'SALE') {
      bump(lotId, m.productId, m.location, absQty, m.unitCost);
    } else if (
      type === STOCK_MOVEMENT_TYPES.SALE_RETURN ||
      type === 'SALE_RETURN' ||
      type === 'STORNO'
    ) {
      const prev = byLot.get(lotId);
      if (prev) {
        prev.quantity = Math.max(0, prev.quantity - absQty);
        if (prev.quantity <= 0) byLot.delete(lotId);
      }
    }
  }

  if (byLot.size > 0) {
    return [...byLot.values()].filter((d) => d.quantity > 0);
  }

  // Fallback: sale line usedLots / lotId (may miss FIFO spillover)
  const products = Array.isArray(sale?.products) ? sale.products : [];
  for (const product of products) {
    const active = getSaleLineActiveQuantity(product);
    if (!(active > 0)) continue;
    const location = product.stockLocation || sale.location || 'Principal';
    const used = Array.isArray(product.usedLots) ? product.usedLots : [];
    if (used.length > 0) {
      const usedTotal = used.reduce((s, u) => s + (Number(u.quantity) || 0), 0) || 1;
      for (const u of used) {
        const share = (Number(u.quantity) || 0) / usedTotal;
        bump(
          u.lotId,
          product.productId,
          u.location || location,
          Math.round(active * share * 1000) / 1000
        );
      }
    } else if (product.lotId) {
      bump(product.lotId, product.productId, location, active);
    }
  }

  return [...byLot.values()].filter((d) => d.quantity > 0);
}

/**
 * Allocate restore qty for a product across remaining lot deductions (FIFO by list order).
 */
function takeDeductionsForProduct(remaining, productId, quantity) {
  let left = Number(quantity) || 0;
  const taken = [];
  if (!(left > 0)) return taken;

  for (const d of remaining) {
    if (!(left > 0)) break;
    if (!sameProductId(d.productId, productId) || !(d.quantity > 0)) continue;
    const use = Math.min(d.quantity, left);
    taken.push({
      lotId: d.lotId,
      productId: d.productId,
      location: d.location,
      quantity: use
    });
    d.quantity -= use;
    left -= use;
  }
  return taken;
}

/**
 * @param {object} sale
 * @param {object} parsed
 * @param {{ mode: 'full' } | { mode: 'lines', lines: Array<{ lineKey?: string, productId?: *, quantity: number }> }} request
 */
export function planSaleVoid(sale, parsed, request) {
  if (!sale) {
    return { success: false, error: 'sale_missing' };
  }
  if (isSaleFullyCancelled(sale)) {
    return { success: false, error: 'already_voided' };
  }

  const products = Array.isArray(sale.products) ? [...sale.products] : [];
  const catalogProducts = Array.isArray(parsed?.products) ? parsed.products : [];
  // Prefer live shell recipes; sale.recipes only if checkout still attached them (legacy).
  const recipes = Array.isArray(parsed?.recipes)
    ? parsed.recipes
    : Array.isArray(sale?.recipes)
      ? sale.recipes
      : [];
  const remainingDeductions = collectRemainingSaleDeductions(
    sale,
    parsed?.stockMovements
  ).map((d) => ({ ...d }));

  /** @type {Array<{ lineKey: string, productId: *, quantity: number, amount: number }>} */
  const voidLines = [];
  /** @type {Array<{ lotId: string, productId: *, location: string, quantity: number }>} */
  const restores = [];

  if (request?.mode === 'full') {
    products.forEach((product, index) => {
      const qty = getSaleLineActiveQuantity(product);
      if (!(qty > 0)) return;
      const key = lineKey(product, index);
      voidLines.push({
        lineKey: key,
        productId: product.productId,
        quantity: qty,
        amount: qty * getSaleLineUnitPrice(product)
      });
      const includeLinks = resolveVoidDishIncludeLinks(product, catalogProducts, recipes);
      if (includeLinks) {
        for (const link of includeLinks) {
          restores.push(
            ...takeDeductionsForProduct(
              remainingDeductions,
              link.productId,
              link.quantity * qty
            )
          );
        }
      } else {
        restores.push(...takeDeductionsForProduct(remainingDeductions, product.productId, qty));
      }
    });
    // Any leftover SALE movements (FIFO spillover not on lines) still restore on full void
    for (const d of remainingDeductions) {
      if (!(d.quantity > 0)) continue;
      restores.push({
        lotId: d.lotId,
        productId: d.productId,
        location: d.location,
        quantity: d.quantity
      });
      d.quantity = 0;
    }
    // No product lines: synthesize a void line from sale total
    if (voidLines.length === 0 && !isSaleFullyCancelled(sale)) {
      const amount = Number(sale.totalAmount ?? sale.total ?? 0) || 0;
      voidLines.push({
        lineKey: 'full',
        productId: null,
        quantity: 1,
        amount
      });
    }
  } else if (request?.mode === 'lines') {
    const specs = Array.isArray(request.lines) ? request.lines : [];
    if (specs.length === 0) {
      return { success: false, error: 'lines_required' };
    }

    for (const spec of specs) {
      const wantQty = Number(spec.quantity);
      if (!Number.isFinite(wantQty) || wantQty <= 0) {
        return { success: false, error: 'invalid_line_quantity' };
      }

      let index = -1;
      if (spec.lineKey != null && spec.lineKey !== '') {
        index = products.findIndex((p, i) => lineKey(p, i) === String(spec.lineKey));
      }
      if (index < 0 && spec.productId != null) {
        index = products.findIndex(
          (p, i) =>
            sameProductId(p.productId, spec.productId) && getSaleLineActiveQuantity(p) > 0
        );
      }
      if (index < 0) {
        return { success: false, error: 'line_not_found', productId: spec.productId };
      }

      const product = products[index];
      const active = getSaleLineActiveQuantity(product);
      if (wantQty > active + 1e-9) {
        return {
          success: false,
          error: 'void_qty_exceeds_active',
          productId: product.productId,
          active,
          requested: wantQty
        };
      }

      const key = lineKey(product, index);
      voidLines.push({
        lineKey: key,
        productId: product.productId,
        quantity: wantQty,
        amount: wantQty * getSaleLineUnitPrice(product)
      });
      const includeLinks = resolveVoidDishIncludeLinks(product, catalogProducts, recipes);
      if (includeLinks) {
        for (const link of includeLinks) {
          restores.push(
            ...takeDeductionsForProduct(
              remainingDeductions,
              link.productId,
              link.quantity * wantQty
            )
          );
        }
      } else {
        restores.push(
          ...takeDeductionsForProduct(remainingDeductions, product.productId, wantQty)
        );
      }
    }
  } else {
    return { success: false, error: 'invalid_mode' };
  }

  if (voidLines.length === 0) {
    return { success: false, error: 'nothing_to_void' };
  }

  const voidAmount = voidLines.reduce((s, l) => s + l.amount, 0);
  const activeTotalBefore = computeSaleActiveTotal(sale);
  const activeTotalAfter = Math.max(0, activeTotalBefore - voidAmount);
  const unpaidBefore = resolveSaleUnpaidAmount(sale);
  const unpaidAfter =
    activeTotalBefore > 0
      ? Math.round(unpaidBefore * (activeTotalAfter / activeTotalBefore) * 100) / 100
      : 0;
  const balanceDelta = -(unpaidBefore - unpaidAfter);

  const touchedStockKeys = new Set();
  for (const r of restores) {
    touchedStockKeys.add(`${r.productId}|${r.location || 'Principal'}`);
  }

  const willCancelFully =
    request?.mode === 'full' ||
    (activeTotalAfter <= 1e-9 &&
      products.every((p, i) => {
        const voidedNow = voidLines
          .filter((v) => v.lineKey === lineKey(p, i))
          .reduce((s, v) => s + v.quantity, 0);
        return getSaleLineActiveQuantity(p) - voidedNow <= 1e-9;
      }));

  return {
    success: true,
    mode: request.mode,
    voidLines,
    restores,
    voidAmount,
    activeTotalBefore,
    activeTotalAfter,
    unpaidBefore,
    unpaidAfter,
    balanceDelta,
    willCancelFully,
    touchedStockKeys
  };
}

function applyLotRestoreToParsed(parsed, lotId, quantity) {
  // Dead dual-write helper kept only for reference; void uses SALE_RETURN projection.
  const err = new Error(
    'ADR-0016: applyLotRestoreToParsed retired — use SALE_RETURN movements with projectLots'
  );
  err.code = 'LOT_QTY_WITHOUT_MOVEMENT';
  throw err;
}

function refreshStockKeys(parsed, touchedStockKeys) {
  if (!parsed.stockData || typeof parsed.stockData !== 'object') {
    parsed.stockData = {};
  }
  ensureLotsArray(parsed);
  for (const key of touchedStockKeys || []) {
    const [productId, location] = String(key).split('|');
    const loc = location || 'Principal';
    const lots = (parsed.lots || []).filter(
      (l) =>
        sameProductId(l.productId, productId) &&
        String(l.location || 'Principal').trim().toLowerCase() ===
          String(loc).trim().toLowerCase()
    );
    parsed.stockData[`${productId}_${loc}`] = {
      ...projectStockFromLots(productId, loc, lots),
      productId: Number(productId),
      location: loc,
      lastCalculated: new Date().toISOString(),
      source: 'sale_void_atomic'
    };
    const allLots = (parsed.lots || []).filter((l) =>
      sameProductId(l.productId, productId)
    );
    parsed.stockData[`${productId}_all`] = projectStockFromLots(productId, null, allLots);
  }
}

/**
 * Mutates parsed: lots, stockData, stockMovements, customerDebts, sales[].
 * @returns {{ sale: object, balanceChange: object|null }}
 */
export function applySaleVoidToParsed(parsed, sale, plan, meta = {}) {
  if (!plan?.success) {
    throw new Error(plan?.error || 'void_plan_failed');
  }

  ensureLotsArray(parsed);
  // ADR-0016 Phase 4: SALE_RETURN movements project lots (no prior restore mutate)
  const referenceId = sale.orderId || sale.id;
  const correlationId =
    sale.inventoryCorrelationId ||
    sale.correlationId ||
    sale.costSnapshot?.inventoryCorrelationId ||
    (referenceId != null ? `corr_${referenceId}` : undefined);
  const returnMovements = (plan.restores || []).map((d) =>
    normalizeStockMovement({
      movementType: STOCK_MOVEMENT_TYPES.SALE_RETURN,
      productId: d.productId,
      lotId: d.lotId,
      quantity: Math.abs(Number(d.quantity)),
      referenceId,
      referenceType: 'sale',
      correlationId,
      location: d.location || sale.location || 'Principal',
      storeId: sale.storeId || d.storeId,
      unitCost: Number(d.unitCost ?? d.unitPrice ?? d.purchaseUnitPrice) || 0,
      costMethod: sale.costMethod || 'FIFO',
      costMethodVersion: sale.costMethodVersion || 1,
      packaging:
        d.packaging ||
        packagingAuditMetaFromCatalog(d) ||
        packagingAuditMetaFromCatalog({
          productsPerPackage: d.productsPerPackage,
          unitsPerPack: d.unitsPerPack,
          packagingType: d.packagingType
        }),
      note: plan.mode === 'full' ? 'sale_void_full' : 'sale_void_line',
      actorId: meta.actorId,
      sessionId: meta.sessionId
    })
  );
  appendStockMovements(parsed, returnMovements, {
    businessId: meta.businessId || null,
    defaultCorrelationId: correlationId,
    projectLots: true,
    actorId: meta.actorId || sale.workerId || sale.actorId || null,
    sessionId: meta.sessionId || sale.sessionId || sale.cashRegisterSessionId || null
  });
  refreshStockKeys(parsed, plan.touchedStockKeys);

  const now = meta.at || new Date().toISOString();
  const products = Array.isArray(sale.products) ? sale.products.map((p) => ({ ...p })) : [];

  for (const vl of plan.voidLines) {
    const idx = products.findIndex((p, i) => lineKey(p, i) === vl.lineKey);
    if (idx < 0) continue;
    const prevVoided = Math.max(0, Number(products[idx].voidedQuantity) || 0);
    products[idx] = {
      ...products[idx],
      voidedQuantity: prevVoided + vl.quantity
    };
  }

  let nextInventoryCost = sale.inventoryCost;
  if (sale.inventoryCost != null && Number.isFinite(Number(sale.inventoryCost))) {
    const before = Number(plan.activeTotalBefore) || 0;
    const after = Number(plan.activeTotalAfter) || 0;
    nextInventoryCost =
      before > 0
        ? (Number(sale.inventoryCost) || 0) * (after / before)
        : 0;
  }

  let nextSale = {
    ...sale,
    products,
    unpaidAmount: plan.unpaidAfter,
    creditAmount: plan.unpaidAfter,
    voidedAmount: (Number(sale.voidedAmount) || 0) + plan.voidAmount,
    updatedAt: now
  };
  const scaleFrozenCost = shouldScaleFrozenInventoryCost(sale);
  if (nextInventoryCost != null && scaleFrozenCost) {
    nextSale.inventoryCost = nextInventoryCost;
    nextSale.inventoryCostFrozen = true;
    if (sale.costSnapshot && typeof sale.costSnapshot === 'object') {
      nextSale.costSnapshot = {
        ...sale.costSnapshot,
        inventoryCost: nextInventoryCost,
        extendedCost: nextInventoryCost,
        frozen: true
      };
    }
    if (sale.restaurantInventoryCost === true || isRestaurantScopedSale(sale)) {
      nextSale.restaurantInventoryCost = true;
    }
  }

  if (plan.willCancelFully) {
    nextSale = {
      ...nextSale,
      status: 'cancelled',
      voided: true,
      voidedAt: now,
      voidReason: meta.reason || '',
      voidedBy: meta.actor || null,
      unpaidAmount: 0,
      creditAmount: 0,
      inventoryCost: scaleFrozenCost ? 0 : sale.inventoryCost,
      inventoryCostFrozen: scaleFrozenCost ? true : sale.inventoryCostFrozen,
      restaurantInventoryCost: scaleFrozenCost
        ? sale.restaurantInventoryCost === true || isRestaurantScopedSale(sale)
        : sale.restaurantInventoryCost
    };
    if (scaleFrozenCost && sale.costSnapshot && typeof sale.costSnapshot === 'object') {
      nextSale.costSnapshot = {
        ...sale.costSnapshot,
        inventoryCost: 0,
        extendedCost: 0,
        frozen: true
      };
    }
  } else {
    nextSale.status = sale.status || 'completed';
    nextSale.lastPartialVoidAt = now;
    nextSale.lastPartialVoidReason = meta.reason || '';
  }

  // total / totalAmount always from active lines (never leave stale plan vs product drift)
  Object.assign(nextSale, resolvePersistedSaleTotals(nextSale));

  let balanceChange = null;
  if (plan.balanceDelta !== 0 && nextSale.customerId) {
    balanceChange = applyCustomerBalanceDelta(parsed, {
      customerId: nextSale.customerId,
      customerName: nextSale.customerName || '',
      delta: plan.balanceDelta,
      previousBalance: 0,
      nextBalance: 0,
      saleId: nextSale.id || nextSale.orderId
    });
  }

  const sales = Array.isArray(parsed.sales) ? [...parsed.sales] : [];
  const saleIdx = sales.findIndex(
    (s) =>
      (sale.orderId && s.orderId === sale.orderId) ||
      (sale.id && s.id === sale.id)
  );
  if (saleIdx >= 0) {
    sales[saleIdx] = { ...sales[saleIdx], ...nextSale };
  } else {
    sales.push(nextSale);
  }
  parsed.sales = sales;

  return { sale: nextSale, balanceChange, returnMovements };
}

export function dispatchSaleVoidInventoryEvents(plan) {
  for (const r of plan?.restores || []) {
    publishLotUpdated({
      productId: r.productId,
      lotId: r.lotId,
      location: r.location,
      type: 'sale_void_restore',
      quantity: r.quantity
    });
  }
  for (const key of plan?.touchedStockKeys || []) {
    const [productId, location] = String(key).split('|');
    publishLegacyWindowEvent('stockUpdated', {
      productId,
      location,
      type: 'sale_void'
    });
  }
}

export function buildSaleVoidedFact(sale, plan, extras = {}) {
  return {
    type: 'sale_voided',
    sale,
    saleId: sale?.id || sale?.orderId || null,
    customerId: sale?.customerId || null,
    mode: plan?.mode || 'full',
    voidAmount: plan?.voidAmount || 0,
    unpaidDelta: plan?.balanceDelta || 0,
    fullyCancelled: Boolean(plan?.willCancelFully),
    at: new Date().toISOString(),
    ...extras
  };
}

/**
 * Liste-Status: cancelled | modified | pending | completed
 * @param {object} sale
 * @returns {'cancelled'|'modified'|'pending'|'completed'}
 */
export function resolveSaleListStatusKind(sale) {
  if (isSaleFullyCancelled(sale)) return 'cancelled';
  const hasPartialVoid =
    Number(sale?.voidedAmount) > 0 ||
    Boolean(sale?.lastPartialVoidAt) ||
    (Array.isArray(sale?.products) &&
      sale.products.some((p) => Number(p?.voidedQuantity) > 0));
  if (hasPartialVoid) return 'modified';
  const status = String(sale?.status || 'completed').toLowerCase();
  if (status === 'pending' || status === 'processing') return 'pending';
  return 'completed';
}

export { lineKey as saleLineKey };
